import React, { useState, useEffect } from "react";
import api from "../services/api";
import { AlertTriangle, Users, MessageSquare, FolderOpen } from "lucide-react";

// ---- Reusable Progress Warning Card ---- //
const UsageLimitWarning = ({
  feature,
  current = 0,
  limit = -1,
  onUpgrade,
  className = "",
}) => {
  const percentage = limit === -1 ? 0 : (current / limit) * 100;
  const isNearLimit = percentage >= 80 && percentage < 100;
  const isAtLimit = percentage >= 100;

  if (limit === -1) return null; // Unlimited plan

  return (
    <div className={`bg-white border rounded-lg p-4 shadow-sm ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {feature === "customers" && <Users className="w-4 h-4" />}
          {feature === "groups" && <FolderOpen className="w-4 h-4" />}
          {feature === "messages" && <MessageSquare className="w-4 h-4" />}
          <span className="font-medium capitalize">{feature}</span>
        </div>
        <span className="text-sm text-gray-600">
          {current}/{limit === -1 ? "∞" : limit}
        </span>
      </div>

      <div className="w-full bg-gray-200 rounded-full h-2 mb-2 overflow-hidden">
        <div
          className={`h-2 transition-all duration-500 ${
            isAtLimit
              ? "bg-red-500"
              : isNearLimit
              ? "bg-yellow-500"
              : "bg-green-500"
          }`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        ></div>
      </div>

      {isAtLimit && (
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertTriangle className="w-4 h-4" />
          <span>Limit reached</span>
          {onUpgrade && (
            <button
              onClick={onUpgrade}
              className="ml-auto text-blue-600 hover:text-blue-800 font-medium"
            >
              Upgrade Plan
            </button>
          )}
        </div>
      )}

      {isNearLimit && !isAtLimit && (
        <div className="flex items-center gap-2 text-yellow-600 text-sm">
          <AlertTriangle className="w-4 h-4" />
          <span>Near limit ({percentage.toFixed(0)}%)</span>
        </div>
      )}
    </div>
  );
};

// ---- Main Usage Validator Wrapper ---- //
const UsageValidator = ({ children, feature, onLimitExceeded }) => {
  const [usage, setUsage] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsageData();
  }, []);

  const fetchUsageData = async () => {
    try {
      const [usageRes, subRes] = await Promise.all([
        api.get("/subscriptions/usage"),
        api.get("/subscriptions/current"),
      ]);

      if (usageRes.data?.success) setUsage(usageRes.data.usage);
      if (subRes.data?.success) setSubscription(subRes.data.subscription);
    } catch (error) {
      console.error("Error fetching subscription usage:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkLimit = () => {
    if (!usage || !subscription) return true;

    const plan = subscription?.subscriptionPlan;
    if (!plan) return true;

    let current = 0,
      limit = -1;

    switch (feature) {
      case "customers":
        current = usage.customers;
        limit = plan.maxCustomers;
        break;
      case "groups":
        current = usage.groups;
        limit = plan.maxGroups;
        break;
      case "messages":
        current = usage.messagesThisPlan;
        limit = plan.maxMessages;
        break;
      default:
        return true;
    }

    if (limit === -1) return true; // Unlimited plan

    const canProceed = current < limit;

    if (!canProceed && onLimitExceeded) {
      onLimitExceeded({
        feature,
        current,
        limit,
        planName: plan.name,
      });
    }

    return canProceed;
  };

  if (loading) {
    return (
      <div className="p-4 border rounded-lg bg-white animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-2 bg-gray-200 rounded w-full"></div>
      </div>
    );
  }

  const canProceed = checkLimit();

  return (
    <div>
      {usage && subscription && (
        <UsageLimitWarning
          feature={feature}
          current={
            feature === "messages"
              ? usage.messagesThisPlan
              : usage[feature] ?? 0
          }
          limit={
            subscription.subscriptionPlan[
              `max${feature.charAt(0).toUpperCase() + feature.slice(1)}`
            ]
          }
          onUpgrade={() => (window.location.href = "/subscription-plans")}
          className="mb-4"
        />
      )}
      {React.cloneElement(children, {
        disabled: !canProceed || children.props.disabled,
      })}
    </div>
  );
};

export { UsageLimitWarning, UsageValidator };
export default UsageLimitWarning;
